import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  ProjectStage,
  StageFormData,
  DEFAULT_STAGE_FORM_DATA,
  TeamMember
} from '../../config';
import StageContactField from '../../../StageContactField';
import Dropdown from '../../../Dropdown';
import { DropdownItem } from '../../../Dropdown/config';
import { useConfirmDialog } from '../../../ConfirmDialog';
import PanelHeading from '../../../PanelHeading';
import Field from '../../../Field';
import TextInput from '../../../TextInput';
import WorkflowStatusPill from '../../../WorkflowStatusPill';
import IconButton from '../../../IconButton';
import { EditIcon, TrashIcon, PlusIcon } from '../../../IconButton/icons';
import { useUserRole } from '../../../../utils/useUserRole';
import { errorMessage } from '../../../../utils/supabaseErrors';
import { canUseManagerOnlyStatuses, workflowStatusDropdownItems } from '../../../../utils/workflowStatusAccess';

// =======================================
// INTERFACES
// =======================================

interface WorkflowStatus {
  id: number;
  status_name: string;
  display_order: number;
}

interface StagesTabProps {
  projectId: number;
  projectType: string;
  teamMembers: TeamMember[];
  /** The project's client: a stage's contact is one of its contacts. */
  clientId: number | string | null;
  /** The project's own primary contact, named in the "inherit" entry. */
  projectContactName: string | null;
  onStageCreated?: () => void;
  onStageUpdated?: () => void;
  onStageDeleted?: () => void;
}

interface FormErrors {
  [key: string]: string | undefined;
}

// =======================================
// MAIN COMPONENT
// =======================================

const StagesTab: React.FC<StagesTabProps> = ({
  projectId,
  projectType,
  teamMembers,
  clientId,
  projectContactName,
  onStageCreated,
  onStageUpdated,
  onStageDeleted
}) => {
  // State management
  const [stages, setStages] = useState<ProjectStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingStageId, setEditingStageId] = useState<number | null>(null);
  const [formData, setFormData] = useState<StageFormData>(DEFAULT_STAGE_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingStageId, setDeletingStageId] = useState<number | null>(null);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);

  const supabase = useSupabaseClient();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const canUseManagerOnly = canUseManagerOnlyStatuses(useUserRole());

  // Fetch workflow statuses for the project type
  useEffect(() => {
    const fetchWorkflowStatuses = async () => {
      if (!projectType) return;

      try {
        const { data, error } = await supabase
          .rpc('get_workflow_statuses_for_type', { p_job_type: projectType });

        if (error) {
          console.error('Error fetching workflow statuses:', error);
          return;
        }

        setWorkflowStatuses(data || []);
      } catch (err) {
        console.error('Error fetching workflow statuses:', err);
      }
    };

    fetchWorkflowStatuses();
  }, [projectType, supabase]);

  // Workflow status options. Locked against the status the stage is saved in,
  // not the form's value: picking a locked status must not unlock it.
  const savedStatusId = stages.find(s => s.id === editingStageId)?.workflow_status_id ?? null;
  const statusDropdownItems = useMemo<DropdownItem[]>(
    () => workflowStatusDropdownItems(workflowStatuses, projectType, canUseManagerOnly, savedStatusId),
    [workflowStatuses, projectType, canUseManagerOnly, savedStatusId]
  );

  // Get the default "Probable" status ID for new stages
  const defaultWorkflowStatusId = useMemo(() => {
    const probableStatus = workflowStatuses.find(
      ws => ws.status_name.toLowerCase() === 'probable'
    );
    return probableStatus ? String(probableStatus.id) : '';
  }, [workflowStatuses]);

  // Create dropdown items for team members
  const teamMemberDropdownItems = useMemo<DropdownItem[]>(() => {
    return [
      { id: '', label: 'Inherit from parent project', value: '' },
      ...teamMembers.map(member => ({
        id: member.id.toString(),
        label: `${member.first_name} ${member.last_name}`,
        value: member.id.toString()
      }))
    ];
  }, [teamMembers]);

  // =======================================
  // DATA LOADING
  // =======================================

  const loadStages = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .rpc('get_project_stages', { p_parent_project_id: projectId });

      if (error) {
        console.error('Error loading stages:', error);
        return;
      }

      if (data && !data.error) {
        setStages(data);
      } else {
        console.error('Error in response:', data?.error);
        setStages([]);
      }
    } catch (error) {
      console.error('Error loading stages:', error);
      setStages([]);
    } finally {
      setLoading(false);
    }
  }, [supabase, projectId]);

  // Load stages on component mount
  useEffect(() => {
    loadStages();
  }, [loadStages]);

  // =======================================
  // FORM HANDLING
  // =======================================

  const handleInputChange = (field: keyof StageFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.stage_id.trim()) {
      newErrors.stage_id = 'Stage ID is required';
    }

    if (!formData.stage_description.trim()) {
      newErrors.stage_description = 'Stage description is required';
    }

    if (!formData.workflow_status_id) {
      newErrors.workflow_status_id = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData(DEFAULT_STAGE_FORM_DATA);
    setErrors({});
    setEditingStageId(null);
    setIsCreating(false);
  };

  // Start creating a new stage with default "Probable" status
  const startCreatingStage = () => {
    setFormData({
      ...DEFAULT_STAGE_FORM_DATA,
      workflow_status_id: defaultWorkflowStatusId
    });
    setErrors({});
    setEditingStageId(null);
    setIsCreating(true);
  };

  // =======================================
  // CRUD OPERATIONS
  // =======================================

  const handleCreateStage = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const { data, error } = await supabase
        .rpc('create_project_stage', {
          p_parent_project_id: projectId,
          p_stage_id: formData.stage_id.trim(),
          p_stage_description: formData.stage_description.trim(),
          p_project_lead_id: formData.project_lead || null,
          // '' leaves the stage inheriting the project's contact.
          p_primary_contact: formData.primary_contact ? parseInt(formData.primary_contact, 10) : null
        });

      if (error) {
        console.error('Error creating stage:', error);
        setErrors({ general: 'Failed to create stage. Please try again.' });
        return;
      }

      if (data && !data.success) {
        setErrors({ general: data.error || 'Failed to create stage' });
        return;
      }

      // create_project_stage doesn't accept care_of — set it in a follow-up
      // update when provided (data.stage_id is the new stage's id).
      const careOf = formData.care_of?.trim();
      if (careOf && data?.stage_id) {
        const { error: careError } = await supabase.rpc('update_project_stage', {
          p_stage_id: data.stage_id,
          p_stage_data: { care_of: careOf }
        });
        if (careError) console.error('Error setting stage care-of:', careError);
      }

      // Success
      resetForm();
      await loadStages();
      onStageCreated?.();
    } catch (error) {
      console.error('Error creating stage:', error);
      setErrors({ general: 'Failed to create stage. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStage = (stage: ProjectStage) => {
    setFormData({
      stage_id: stage.stage_id,
      stage_description: stage.stage_description,
      project_lead: stage.project_lead_info?.id || '',
      workflow_status_id: stage.workflow_status_id ? String(stage.workflow_status_id) : '',
      care_of: stage.care_of || '',
      // The raw stage value: '' is "inherit", not the project's contact.
      primary_contact: stage.primary_contact != null ? String(stage.primary_contact) : ''
    });
    setErrors({});
    setEditingStageId(stage.id);
    setIsCreating(true);
  };

  const handleUpdateStage = async () => {
    if (!validateForm() || !editingStageId) return;

    try {
      setIsSubmitting(true);

      // Update stage description and project lead
      const updateData: Record<string, string> = {
        stage_description: formData.stage_description.trim(),
        // Always send care_of (empty string clears it) so it can be removed.
        care_of: formData.care_of?.trim() || '',
        // Always sent too: '' puts the stage back to inheriting the project's.
        primary_contact: formData.primary_contact || ''
      };

      if (formData.project_lead) {
        updateData.project_lead = formData.project_lead;
      }

      const { data, error } = await supabase
        .rpc('update_project_stage', {
          p_stage_id: editingStageId,
          p_stage_data: updateData
        });

      if (error) {
        console.error('Error updating stage:', error);
        setErrors({ general: 'Failed to update stage. Please try again.' });
        return;
      }

      if (data && !data.success) {
        setErrors({ general: data.error || 'Failed to update stage' });
        return;
      }

      // Update workflow status separately
      if (formData.workflow_status_id) {
        const { data: statusData, error: statusError } = await supabase
          .rpc('update_workflow_status', {
            p_item_id: editingStageId,
            p_entity_type: 'stage',
            p_new_status_id: parseInt(formData.workflow_status_id, 10)
          });

        if (statusError) {
          console.error('Error updating workflow status:', statusError);
          setErrors({ general: errorMessage(statusError, 'Failed to update status. Please try again.') });
          return;
        }

        if (statusData && !statusData.success) {
          setErrors({ general: statusData.error || 'Failed to update status' });
          return;
        }
      }

      // Success
      resetForm();
      await loadStages();
      onStageUpdated?.();
    } catch (error) {
      console.error('Error updating stage:', error);
      setErrors({ general: 'Failed to update stage. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Ask, then delete. The dialog names the stage so a slip is caught. */
  const handleDeleteStage = async (stage: ProjectStage) => {
    const ok = await confirm({
      title: 'Delete this stage?',
      message: (
        <>
          <strong>{stage.job_number} – {stage.stage_description}</strong> and everything recorded against it will be removed. This cannot be undone.
        </>
      ),
      confirmLabel: 'Delete stage',
      tone: 'danger',
    });
    if (!ok) return;

    setDeletingStageId(stage.id);
    setErrors({});
    try {
      const { data, error } = await supabase
        .rpc('delete_project_stage', { p_stage_id: stage.id });

      if (error) {
        console.error('Error deleting stage:', error);
        setErrors({ general: 'Failed to delete stage. Please try again.' });
        return;
      }

      if (data && !data.success) {
        setErrors({ general: data.error || 'Failed to delete stage' });
        return;
      }

      await loadStages();
      onStageDeleted?.();
    } catch (error) {
      console.error('Error deleting stage:', error);
      setErrors({ general: 'Failed to delete stage. Please try again.' });
    } finally {
      setDeletingStageId(null);
    }
  };

  // =======================================
  // RENDER HELPERS
  // =======================================

  const statusNameFor = (stage: ProjectStage) =>
    workflowStatuses.find(ws => ws.id === stage.workflow_status_id)?.status_name || stage.status;

  const renderStagesList = () => {
    if (loading) {
      return <p className="ui-table__empty">Loading stages…</p>;
    }

    if (stages.length === 0) {
      return (
        <div className="ui-table__empty stages-tab__empty">
          <p>No stages yet. A stage is a separately numbered part of the job, such as a building or a phase, with its own status and lead.</p>
          <button type="button" className="pill-btn pill-btn--primary pill-btn--sm" onClick={startCreatingStage}>
            <PlusIcon size={12} /> Create the first stage
          </button>
        </div>
      );
    }

    return (
      <div className="ui-cards">
        {stages.map(stage => {
          const statusName = statusNameFor(stage);
          const busy = deletingStageId === stage.id;
          return (
            <div key={stage.id} className="ui-card">
              <div className="ui-card__main">
                <div className="ui-card__top">
                  <span className="ui-card__number">{stage.job_number}</span>
                  <span className="ui-card__title">{stage.stage_description}</span>
                </div>
                <div className="ui-card__meta">
                  <span>
                    {stage.project_lead_info?.full_name
                      ? `Lead ${stage.project_lead_info.full_name}`
                      : <span className="is-empty">Lead inherited</span>}
                  </span>
                  <span>Created {new Date(stage.created_at).toLocaleDateString('en-AU')}</span>
                </div>
              </div>
              <div className="ui-card__side">
                <WorkflowStatusPill status={statusName} />
                <span className="row-actions">
                  <IconButton size="sm" icon={<EditIcon />} label={`Edit ${stage.job_number}`} onClick={() => handleEditStage(stage)} disabled={busy} />
                  <IconButton size="sm" tone="danger" icon={<TrashIcon />} label={`Delete ${stage.job_number}`} onClick={() => { void handleDeleteStage(stage); }} disabled={busy} />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStageForm = () => {
    const editing = editingStageId !== null;
    return (
      <div className="stages-tab__form">
        <PanelHeading
          as="h5"
          title={editing ? 'Edit stage' : 'New stage'}
          hint={editing ? undefined : 'The stage ID becomes the suffix on the job number: stage 1 of 12345 is 12345.1.'}
        />

        <div className="pdm-grid pdm-grid--2">
          <Field label="Stage ID" required htmlFor="stage-form-id" error={errors.stage_id}>
            <TextInput
              id="stage-form-id"
              mono
              value={formData.stage_id}
              onChange={(e) => handleInputChange('stage_id', e.target.value)}
              placeholder="1"
              disabled={editing}
              invalid={!!errors.stage_id}
            />
          </Field>
          <Field label="Description" required htmlFor="stage-form-description" error={errors.stage_description}>
            <TextInput
              id="stage-form-description"
              value={formData.stage_description}
              onChange={(e) => handleInputChange('stage_description', e.target.value)}
              placeholder="e.g. Stage 17, Civil"
              invalid={!!errors.stage_description}
            />
          </Field>
          <Field label="Status" required error={errors.workflow_status_id}>
            <Dropdown
              items={statusDropdownItems}
              value={formData.workflow_status_id}
              onChange={(value) => handleInputChange('workflow_status_id', value)}
              placeholder="Select status..."
              searchable={false}
              className={errors.workflow_status_id ? 'error' : ''}
            />
          </Field>
          <Field label="Stage lead" hint="Leave blank to inherit the project lead.">
            <Dropdown
              items={teamMemberDropdownItems}
              value={formData.project_lead}
              onChange={(value) => handleInputChange('project_lead', value)}
              placeholder="Inherit from parent project"
              searchable={true}
            />
          </Field>
          <StageContactField
            className="span-all"
            value={formData.primary_contact || ''}
            onChange={(contactId) => handleInputChange('primary_contact', contactId)}
            clientId={clientId}
            projectContactName={projectContactName}
          />
          <Field label="Invoice care-of" htmlFor="stage-form-care-of" className="span-all" hint="The owner the invoice is addressed to: the paying party.">
            <TextInput
              id="stage-form-care-of"
              value={formData.care_of || ''}
              onChange={(e) => handleInputChange('care_of', e.target.value)}
              placeholder="Leave blank to address the client directly"
            />
          </Field>
        </div>

        {errors.general && <p className="field__error" role="alert">{errors.general}</p>}

        <div className="pill-row pill-row--end">
          <button type="button" className="pill-btn pill-btn--ghost" onClick={resetForm} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="pill-btn pill-btn--primary"
            onClick={editing ? handleUpdateStage : handleCreateStage}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (editing ? 'Saving…' : 'Creating…')
              : (editing ? 'Save stage' : 'Create stage')}
          </button>
        </div>
      </div>
    );
  };

  // =======================================
  // MAIN RENDER
  // =======================================

  return (
    <div className="stages-tab">
      <PanelHeading
        title="Stages"
        meta={!loading ? `${stages.length}` : undefined}
        action={!isCreating && stages.length > 0 ? (
          <button type="button" className="pill-btn pill-btn--primary pill-btn--sm" onClick={startCreatingStage}>
            <PlusIcon size={12} /> New stage
          </button>
        ) : undefined}
      />

      {!isCreating && errors.general && <p className="field__error" role="alert">{errors.general}</p>}

      {isCreating ? renderStageForm() : renderStagesList()}

      {confirmDialog}
    </div>
  );
};

export default StagesTab;
